import { c as _c } from "react/compiler-runtime";
import type { GetHistoryEntry } from '@repo/schema/history';
import { ArrowRight } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { useHistoryEntity } from '../history-utils/entity.ts';
import { getEventLabel } from '../history-utils/labels.ts';
import { getHistoryValue, hasHistoryValue } from '../history-utils/values.ts';
import { HistoryDescription } from './history-description.tsx';
const FieldContext = t0 => {
  const $ = _c(4);
  const {
    entry
  } = t0;
  let t1;
  if ($[0] !== entry.description || $[1] !== entry.event || $[2] !== entry.field) {
    t1 = entry.event !== "status_changed" && entry.field !== null && entry.description ? <span className="mt-0.5 block text-sm leading-5 font-normal tracking-normal text-muted-foreground normal-case">{entry.description}</span> : null;
    $[0] = entry.description;
    $[1] = entry.event;
    $[2] = entry.field;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const StatusChangeDescription = t0 => {
  const $ = _c(3);
  const {
    entry
  } = t0;
  let t1;
  if ($[0] !== entry.description || $[1] !== entry.event) {
    t1 = entry.event === "status_changed" && entry.description ? <p className="mt-1 text-sm leading-5 font-normal text-muted-foreground">{entry.description}</p> : null;
    $[0] = entry.description;
    $[1] = entry.event;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

// Narrow: field caption above a wrapping "old → new" diff (old struck through).
// Mid: own 4-column grid. Wide: subgrid of the table's field → new columns.
export const ChangeLine = t0 => {
  const $ = _c(21);
  const {
    entry
  } = t0;
  const entity = useHistoryEntity();
  let t1;
  if ($[0] !== entry.old_display || $[1] !== entry.old_value) {
    t1 = hasHistoryValue(entry.old_display, entry.old_value);
    $[0] = entry.old_display;
    $[1] = entry.old_value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hasPreviousValue = t1;
  const t2 = entry.field === null && "@xl/history:col-span-4";
  let t3;
  if ($[3] !== t2) {
    t3 = cn("min-w-0 text-xs leading-4 font-medium tracking-wide break-words text-muted-foreground uppercase @xl/history:text-base @xl/history:leading-6 @xl/history:font-normal @xl/history:tracking-normal @xl/history:text-foreground @xl/history:normal-case", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="sr-only">Field: </span>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== entity || $[7] !== entry) {
    t5 = entry.field_label || entry.field || <HistoryDescription entry={entry} fallback={getEventLabel(entry, entity)} />;
    $[6] = entity;
    $[7] = entry;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== entry) {
    t6 = <FieldContext entry={entry} data-sentry-element="FieldContext" data-sentry-source-file="history-change-line.tsx" />;
    $[9] = entry;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== entry || $[16] !== hasPreviousValue) {
    t8 = entry.field === null ? null : <div className="flex min-w-0 flex-wrap items-start gap-x-2 gap-y-1 @xl/history:contents"><div className={cn("min-w-0 text-base leading-6 break-words text-muted-foreground line-through @4xl/history:no-underline", !hasPreviousValue && "hidden no-underline @xl/history:block")}><span className="sr-only">Previous value: </span>{getHistoryValue(entry.old_display, entry.old_value)}</div><div className="flex min-w-0 items-start gap-2 @xl/history:contents"><span className="flex h-6 shrink-0 items-center justify-self-center text-muted-foreground"><ArrowRight aria-hidden="true" className="size-4 @xl/history:size-6" /></span><div className="min-w-0 text-base leading-6 font-semibold break-words"><span className="sr-only">New value: </span>{getHistoryValue(entry.new_display, entry.new_value)}<StatusChangeDescription entry={entry} /></div></div></div>;
    $[15] = entry;
    $[16] = hasPreviousValue;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="grid min-w-0 gap-y-1 @xl/history:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_minmax(0,1.3fr)] @xl/history:gap-x-2 @4xl/history:col-span-4 @4xl/history:grid-cols-subgrid" data-sentry-component="ChangeLine" data-sentry-source-file="history-change-line.tsx">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};